import React, { FC, useCallback, useMemo } from 'react';

import { Box, Flex, Heading } from '@rover/kibble/core';
import type { Pet } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import PetCounterBody, { PetCounterBodyProps } from './PetCounterBody';
import PetSelectorBody from './PetSelectorBody';

export interface InlinePetCounterProps extends PetCounterBodyProps {
  pets: Pet[];
  titleText: string;
  subtitleText?: string | null;
  searchFilters: SearchFilters;
  onPetSelectionChange: (filters: Partial<SearchFilters>) => void;
}

const InlinePetCounter: FC<InlinePetCounterProps> = ({
  pets,
  dogs,
  cats,
  puppies,
  availablePets,
  triggeredFrom,
  setDogs,
  setPuppies,
  setCats,
  titleText,
  subtitleText,
  searchFilters,
  onPetSelectionChange,
  iconVariant,
}) => {
  const hasPets = pets.length > 0;

  const selectedPets = useMemo(() => searchFilters.pet || [], [searchFilters.pet]);

  const handlePetSelect = useCallback(
    (pet: Pet) => {
      const petId = pet.pk.toString();
      const currentSelectedPets = selectedPets;
      const newPets = currentSelectedPets.includes(petId)
        ? currentSelectedPets.filter((p) => p !== petId)
        : [...currentSelectedPets, petId];

      const newPetOptions = pets.filter((p) => newPets.includes(p.pk.toString()));
      const newPetTypes = [...new Set(newPetOptions.map((p) => p.petType))];
      onPetSelectionChange({ pet: newPets, petType: newPetTypes });
    },
    [selectedPets, pets, onPetSelectionChange]
  );

  return (
    <Box>
      <Flex flexDirection="column" paddingBottom="6x">
        <Heading size="200" textAlign="left" mb="4x">
          {titleText}
        </Heading>
        {subtitleText && (
          <Heading size="100" fontWeight="regular" textColor="secondary" mb="1x">
            {subtitleText}
          </Heading>
        )}
        {hasPets ? (
          <PetSelectorBody pets={pets} selectedPets={selectedPets} onPetSelect={handlePetSelect} />
        ) : (
          <PetCounterBody
            dogs={dogs}
            cats={cats}
            puppies={puppies}
            availablePets={availablePets}
            triggeredFrom={triggeredFrom}
            setDogs={setDogs}
            setPuppies={setPuppies}
            setCats={setCats}
            iconVariant={iconVariant}
          />
        )}
      </Flex>
    </Box>
  );
};

export default InlinePetCounter;
